import * as d3 from "d3"
import styles from "../LineChart/LineChart.module.scss"
import { useEffect, useMemo } from "react"
import LineComponent from "../ChartComponents/LineComponent/LineComponent"
import { Line_CHART_CATEGORY, lineSizeObject } from "../constants"
import { drawAxis, getTextDimensions, convertMinutesToInterval } from "../helper"
import { createAxis, lineGenerator, timeXAxisLabel } from "../LineChart/helper"
import TimeseriesMousePointer from "../ChartComponents/Tooltip/Timeseries/TimeseriesMousePointer"
import {
  ANALYTIC_RESULT_LINE,
  LINE_TYPE,
  REFERENCE_LINE_TYPE
} from "src/components/shared/Charts/Helper/ChartCategories/lineChart"
import NodeComponent from "../ChartComponents/NodeComponent/NodeComponent"
import { isNumber, sortBy, toNumber } from "lodash"

const svgPadding = { top: 50, right: 50, bottom: 50, left: 50 }

const LineChart = (props) => {
  const {
    start,
    end,
    data = {},
    height,
    width,
    minorGridCount = 5,
    majorGridCount = 20,
    bcColor = false,
    chartProps = {},
    chartCategory = Line_CHART_CATEGORY,
    isHeadLess = false,
    markerTypeFrequency = [],
    grids = false
  } = props

  const { legends = {}, calculatedInterval = 15, referenceLegendTimestamp } = chartProps

  const axisList = useMemo(
    () =>
      (chartProps?.axisList || [])?.map((obj) => {
        let calMin = Infinity
        let calMax = -Infinity
        Object.keys(legends)?.forEach((id) => {
          const legendObj = legends?.[id]
          if (legendObj?.axisName === obj?.axisName) {
            let lData = data?.[legendObj?.id]?.data || []
            if (legendObj?.type === REFERENCE_LINE_TYPE) {
              lData = [
                isFinite(toNumber(legendObj?.value))
                  ? toNumber(legendObj?.value)
                  : null
              ]
            } else if (legendObj?.type === ANALYTIC_RESULT_LINE) {
              lData = Object.values(legendObj?.value || {})
            }
            const calculatedRange = d3.extent(lData) || []
            const min = calculatedRange?.[0]
            const max = calculatedRange?.[1]
            //@ts-ignore
            if (min < calMin) {
              //@ts-ignore
              calMin = min
            }
            //@ts-ignore
            if (max > calMax) {
              //@ts-ignore
              calMax = max
            }
          }
        })
        const range = [calMin, calMax]
        return {
          ...obj,
          defaultRange: range,
          range: range
        }
      }),
    [chartProps?.axisList, legends, data]
  )

  const { dynamicInterval } = useMemo(() => {
    const dynamicInterval = convertMinutesToInterval(calculatedInterval)

    return {
      dynamicInterval
    }
  }, [start, end, calculatedInterval])

  const { mgWidth, mgHeight, yScaleRangeMin, yScaleRangeMax } = useMemo(() => {
    const mgWidth = width - svgPadding.left - svgPadding.right + 10
    const mgHeight = height - svgPadding.top - svgPadding.bottom + 10
    const yScaleRangeMin = 0
    const yScaleRangeMax = mgHeight

    return {
      mgWidth,
      mgHeight,
      yScaleRangeMin,
      yScaleRangeMax
    }
  }, [width, height])

  const {
    xScale,
    xAxis1,
    xAxis2,
    verticalMajorGrid,
    verticalMinorGrid,
    mousePointerX
  } = useMemo(() => {
    // Set up X-Axis scale
    const xScale = d3
      .scaleTime()
      .domain([new Date(start), new Date(end)])
      .range([0, mgWidth])

    // Draw x-axes
    const xAxis1 = d3
      .axisBottom(xScale)
      .ticks(5)
      .tickSize(0)
      .tickPadding(10)
      .tickFormat(timeXAxisLabel)
    const xAxis2 = d3
      .axisTop(xScale)
      .ticks(5)
      .tickSize(0)
      .tickPadding(20)
      .tickFormat(timeXAxisLabel)

    const verticalMajorGrid = d3
      .axisBottom(xScale)
      // @ts-ignore
      .tickSize(yScaleRangeMax, 0, 0)
      // @ts-ignore
      .tickFormat("")
      .ticks(minorGridCount)
    const verticalMinorGrid = d3
      .axisBottom(xScale)
      // @ts-ignore
      .tickSize(yScaleRangeMax, 0, 0)
      // @ts-ignore
      .tickFormat("")
      .ticks(majorGridCount)
    const mousePointerX = xScale(new Date(start))
    return {
      xScale,
      xAxis1,
      xAxis2,
      verticalMajorGrid,
      verticalMinorGrid,
      mousePointerX
    }
  }, [start, end, mgWidth, yScaleRangeMax, minorGridCount, majorGridCount])

  const {
    updatedLegends,
    updatedReferenceLegends,
    legendArray,
    addedAxisList,
    horizontalMinorGrid
  } = useMemo(() => {
    // @ts-ignore
    const addedAxisList = axisList?.map((obj) => {
      const scale = d3
        .scaleLinear()
        .range([yScaleRangeMax, yScaleRangeMin])
        .domain([obj?.range?.[0], obj?.range?.[1]])
      const draw = createAxis(obj, scale)
      return {
        ...obj,
        scale,
        drawAxis: draw?.axis,
        className: draw?.className
      }
    })
    const horizontalMinorGridScale = addedAxisList?.find(
      (obj) => obj?.axisName === "y1"
    )?.scale

    const horizontalMinorGrid = d3
      .axisRight(horizontalMinorGridScale)
      .ticks(10)
      // @ts-ignore
      .tickSize(mgWidth, 0, 0)
      // @ts-ignore
      .tickFormat("")

    const updatedLegends = {}
    const updatedReferenceLegends = {}
    const legendArray = []
    Object.keys(legends)?.map((key) => {
      const obj = legends[key]
      const axisObj = addedAxisList?.find(
        (axisObj) => axisObj?.axisName === obj?.axisName
      )
      const yScale = axisObj?.scale
      const newObj = {
        ...obj,
        range: axisObj?.range,
        yScale,
        symbolList: axisObj?.symbol
      }
      if (obj?.type === LINE_TYPE || obj?.type === REFERENCE_LINE_TYPE) {
        legendArray.push(newObj)
      }

      if (obj?.type === REFERENCE_LINE_TYPE) {
        updatedReferenceLegends[key] = newObj
      }
      updatedLegends[key] = newObj
    })

    return {
      updatedLegends,
      updatedReferenceLegends,
      legendArray,
      addedAxisList,
      horizontalMinorGrid
    }
  }, [axisList, legends, yScaleRangeMax, yScaleRangeMin, mgWidth])

  // PDF and IMAGE Export Color control
  useEffect(() => {
    if (bcColor) {
      const textElements = document.querySelectorAll("svg text")
      textElements.forEach((textElement) => {
        textElement.setAttribute("fill", "black")
      })
    }
  }, [bcColor])
  if (!data) {
    return <></>
  }
  return (
    <>
      <TimeseriesMousePointer
        chartCategory={chartCategory}
        xScale={xScale}
        x={mousePointerX}
        y={0}
        data={data}
        legends={legendArray}
        interval={dynamicInterval}
        width={width}
        height={height}
        mgHeight={mgHeight}
        mgWidth={mgWidth}
        yScaleRangeMax={yScaleRangeMax}
        timeLineHeight={0}
      >
        <g data-testid="line-chart-x-axis">
          <g
            // className={styles.xAxis}
            ref={(ref) => drawAxis(ref, xAxis1)}
            style={{ transform: `translate(0,${yScaleRangeMax}px)` }}
            data-testid="line-chart-bottom-x-axis"
          ></g>
          {/* <g
                        data-testid="line-chart-top-x-axis"
                        className={styles.xAxis}
                        ref={(ref) => drawAxis(ref, xAxis2)}
                    ></g> */}
        </g>

        {grids && (
          <g data-testid="line-chart-grids">
            {/* Horizontal Grids */}
            <g
              className={styles.minor}
              ref={(ref) => drawAxis(ref, horizontalMinorGrid)}
              data-testid="line-chart-horizontal-minor-grid"
            ></g>
            {/* Vertical Grids */}
            <g
              className={styles.minor}
              ref={(ref) => drawAxis(ref, verticalMinorGrid)}
              data-testid="line-chart-vertical-minor-grid"
            ></g>
            <g
              className={styles.major}
              ref={(ref) => drawAxis(ref, verticalMajorGrid)}
              data-testid="line-chart-vertical-major-grid"
            ></g>
          </g>
        )}

        <g data-testid="line-chart-y-axis">
          {addedAxisList?.map((obj, i) => {
            // This is temprory solutions
            const { width } = getTextDimensions(
              obj?.symbol?.length > 8
                ? obj?.symbol?.slice(0, 3) + "..."
                : obj?.symbol
            )
            const x =
              obj?.axisName === "y1"
                ? -width
                : obj?.axisName === "y2"
                ? mgWidth + 15
                : obj?.axisName === "y3"
                ? width / 2
                : mgWidth - 35
            const y = -8

            const rightAxisStyle =
              obj?.axisName === "y2" || obj?.axisName === "y4"
                ? {
                    transform: `translate(${mgWidth}px, 0px)`
                  }
                : null

            return isHeadLess && obj?.hideAxis ? (
              <> </>
            ) : (
              <g key={i}>
                <g
                  style={rightAxisStyle}
                  className={obj?.className}
                  ref={(ref) => drawAxis(ref, obj?.drawAxis)}
                  data-testid={`line-chart-${obj?.axisName}-axis`}
                ></g>
                <text
                  className={styles.symbol}
                  x={x}
                  y={y}
                  data-testid={`line-chart-${obj?.axisName}-symbol`}
                >
                  {obj?.symbol?.length > 8
                    ? obj?.symbol?.slice(0, 3) + "..."
                    : obj?.symbol}
                  {obj?.symbol?.length > 8 && <title>{obj?.symbol}</title>}
                </text>
              </g>
            )
          })}
        </g>

        <g data-testid="line-chart-data" clipPath="url(#chart-line-area-clip)">
          {sortBy(Object.values(updatedLegends), "name")?.map((obj: any, i) => {
            const yScale = obj?.yScale
            const strokeWidth = lineSizeObject[obj?.strokeWidth]

            if (obj?.show) {
              const legendData = data?.[obj?.id]?.data || []
              const timestampsData = data?.[obj?.id]?.timestamps || []
              return (
                <g key={i} data-testid={`line-chart-data-group-${i}`}>
                  <LineComponent
                    lineType={obj?.lineType}
                    fill="none"
                    color={obj?.color}
                    strokeWidth={strokeWidth}
                    d={lineGenerator(
                      timestampsData,
                      legendData,
                      yScale,
                      xScale,
                      obj?.type
                    )}
                    dataTestID={`line-chart-line-path-element-${i}`}
                  />
                  <>
                    {markerTypeFrequency?.map((ts, i) => {
                      const nodeCX = ts ? xScale(new Date(ts)) : 0
                      const index = timestampsData.indexOf(ts)
                      const value = legendData?.[index]
                      const nodeCY = obj?.yScale ? obj?.yScale(value) : ""
                      const visibility =
                        isNumber(nodeCX) && isNumber(nodeCY) && obj?.show
                          ? "visible"
                          : "hidden"
                      return (
                        <NodeComponent
                          key={`${i}-${obj?.shape}`}
                          shape={obj?.shape}
                          size={obj?.shapeSize}
                          color={obj?.color}
                          cx={nodeCX}
                          cy={nodeCY}
                          visibility={visibility}
                        />
                      )
                    })}
                  </>
                </g>
              )
            } else {
              return <></>
            }
          })}
          {Object.keys(updatedReferenceLegends)?.map((key, i) => {
            const obj = updatedReferenceLegends[key]
            const yScale = obj?.yScale
            const strokeWidth = lineSizeObject[obj?.strokeWidth]
            if (obj?.show) {
              return (
                <g key={i} data-testid={`line-chart-data-group-${i}`}>
                  <LineComponent
                    lineType={obj?.lineType}
                    fill="none"
                    color={obj?.color}
                    strokeWidth={strokeWidth}
                    d={lineGenerator(
                      referenceLegendTimestamp,
                      obj?.value,
                      yScale,
                      xScale,
                      obj?.type
                    )}
                    dataTestID={`line-chart-line-path-element-${i}`}
                  />
                </g>
              )
            } else {
              return <></>
            }
          })}
        </g>
      </TimeseriesMousePointer>
    </>
  )
}

export default LineChart
